import React, { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { usePageHeader } from '@/hooks/usePageHeader';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { AlertTriangle } from 'lucide-react';

// ---------------------------------------------------------------------------
// Error Boundary (class component — React requires it)
// ---------------------------------------------------------------------------

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class PageErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[RorosPage] Uncaught error in child component:', error, info);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="py-12 px-4">
          <Alert variant="error" title="Something went wrong">
            <div className="space-y-3">
              <p>An unexpected error occurred while rendering this page.</p>
              {this.state.error && (
                <details className="text-xs">
                  <summary className="cursor-pointer font-medium">Error details</summary>
                  <pre className="mt-2 whitespace-pre-wrap break-words bg-error-50 p-2 rounded text-error-800">
                    {this.state.error.message}
                  </pre>
                </details>
              )}
              <div>
                <Button size="sm" onClick={this.handleReload}>
                  <AlertTriangle className="h-4 w-4 mr-1" />
                  Reload Page
                </Button>
              </div>
            </div>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}

// ---------------------------------------------------------------------------
// Breadcrumb types
// ---------------------------------------------------------------------------

export interface RorosBreadcrumb {
  label: string;
  path: string;
}

// ---------------------------------------------------------------------------
// RorosPage component
// ---------------------------------------------------------------------------

export interface RorosPageProps {
  /** Page title rendered in the PageHeader */
  title: string;
  /** Optional icon (e.g. a Lucide icon component) */
  icon?: ReactNode;
  /** Breadcrumbs — array of { label, path } rendered as clickable links */
  breadcrumbs?: RorosBreadcrumb[];
  /** Action buttons rendered top-right in the PageHeader */
  actions?: ReactNode;
  /** Additional CSS classes for the content area */
  className?: string;
  /**
   * When true, makes the page fill its parent's full height using flex-column.
   * Use this when the route is in fullHeight mode (e.g., Control Tower board).
   * The outer wrapper becomes `h-full flex flex-col` and the content area
   * becomes `flex-1 min-h-0` so children can use `h-full`.
   */
  fullHeight?: boolean;
  /** Page content */
  children: ReactNode;
}

/**
 * RorosPage — Enforces consistent page structure for every ROROS page.
 *
 * Provides:
 * - usePageHeader hook (sets chrome title, icon, breadcrumbs)
 * - Optional actions toolbar rendered above content
 * - Error boundary wrapping children
 *
 * @example
 * ```tsx
 * <RorosPage
 *   title="Work Items"
 *   icon={<Briefcase />}
 *   breadcrumbs={[{ label: 'Control Tower', path: '/roros/control-tower' }]}
 *   actions={<Button>Create</Button>}
 * >
 *   <WorkItemContent />
 * </RorosPage>
 * ```
 */
export function RorosPage({ title, icon, breadcrumbs, actions, className, children, fullHeight }: RorosPageProps) {
  usePageHeader({ title, icon, breadcrumbs });

  return (
    <div className={cn(
      'px-4 py-6 sm:px-6 lg:px-8 lg:py-8',
      fullHeight && 'h-full flex flex-col',
    )}>
      {/* Actions toolbar — rendered above content when provided */}
      {actions && (
        <div className={cn('flex items-center justify-end mb-4', fullHeight && 'flex-shrink-0')}>
          {actions}
        </div>
      )}
      {/* Content wrapped in error boundary */}
      <PageErrorBoundary>
        <div className={cn(className, fullHeight && 'flex-1 min-h-0')}>
          {children}
        </div>
      </PageErrorBoundary>
    </div>
  );
}
